import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  Switch,
  Txt,
  toast,
} from "@repo/ui";
import { formatLocalDateTime } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import {
  PiBuildingOffice,
  PiCopy,
  PiPlus,
  PiTrash,
  PiUser,
} from "react-icons/pi";
import { RiSettings3Line } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  type FeatureFlagConfigDetailsFragment,
  FeatureFlagConfigsDocument,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/feature-flags")({
  head: () => opsHead("Feature Flags"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function toSnakeCase(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

function View() {
  const [newOrgOverrideOpen, setNewOrgOverrideOpen] = useState(false);
  const [newFlagOpen, setNewFlagOpen] = useState(false);
  const [deleteOrgOverrideOpen, setDeleteOrgOverrideOpen] = useState("");
  const [deleteFlagOpen, setDeleteFlagOpen] = useState("");

  graphql(`
    fragment FeatureFlagConfigDetails on FeatureFlagConfiguration {
      id
      name
      defaultValue
      primaryEmployeeValue
      createdAt
      updatedAt
      userOverrides {
        id
        userProfileId
        value
      }
      orgOverrides {
        id
        organisation {
          id
          name
        }
        value
      }
    }
  `);

  const { data } = useSuspenseQuery(
    graphql(`
      query FeatureFlagConfigs {
        organisations {
          id
          name
        }
        featureFlagConfigurations {
          ...FeatureFlagConfigDetails
        }
      }
    `),
  );

  const featureFlags = data?.featureFlagConfigurations?.slice() ?? [];
  featureFlags.sort((a, b) => a.name.localeCompare(b.name));

  const organisations = (data?.organisations ?? []).filter(
    (org) => org.__typename === "Organisation",
  );
  organisations.sort((a, b) => a.name.localeCompare(b.name));

  const [createFeatureFlag] = useMutation(
    graphql(`
      mutation CreateFeatureFlag($input: CreateFeatureFlagInput!) {
        createFeatureFlag(input: $input) {
          featureFlagConfiguration {
            ...FeatureFlagConfigDetails
          }
        }
      }
    `),
    {
      update(cache, { data }) {
        const newFlag = data?.createFeatureFlag?.featureFlagConfiguration;
        if (!newFlag) {
          return;
        }

        cache.updateQuery(
          { query: FeatureFlagConfigsDocument },
          (existingData) => {
            if (!existingData) {
              return null;
            }

            return {
              ...existingData,
              featureFlagConfigurations: [
                ...(existingData.featureFlagConfigurations ?? []),
                newFlag,
              ],
            };
          },
        );
      },
    },
  );

  const [updateFeatureFlag] = useMutation(
    graphql(`
      mutation UpdateFeatureFlag($input: UpdateFeatureFlagInput!) {
        updateFeatureFlag(input: $input) {
          featureFlagConfiguration {
            ...FeatureFlagConfigDetails
          }
        }
      }
    `),
  );

  const [deleteFeatureFlag] = useMutation(
    graphql(`
      mutation DeleteFeatureFlag($input: DeleteFeatureFlagInput!) {
        deleteFeatureFlag(input: $input) {
          featureFlagConfigurations {
            ...FeatureFlagConfigDetails
          }
        }
      }
    `),
    {
      update(cache, { data }) {
        const newFlags = data?.deleteFeatureFlag?.featureFlagConfigurations;
        if (!newFlags) {
          return;
        }

        cache.writeQuery({
          query: FeatureFlagConfigsDocument,
          data: {
            __typename: "Query",
            featureFlagConfigurations: newFlags,
          },
        });
      },
    },
  );

  const handleCreateOrgOverride = async ({
    orgId,
    flagName,
  }: {
    orgId: string;
    flagName: string;
  }) => {
    const existingFlag = featureFlags.find((ff) => ff.name === flagName);
    if (!existingFlag) {
      throw new Error(`couldnt find flag ${flagName}`);
    }

    const organisationOverrides = (existingFlag.orgOverrides ?? []).map(
      (o) => ({
        organisationId: o.organisation?.id ?? "",
        value: o.value,
      }),
    );
    organisationOverrides.push({
      organisationId: orgId,
      value: false,
    });

    await updateFeatureFlag({
      variables: {
        input: {
          flagName,
          defaultValue: existingFlag.defaultValue,
          primaryEmployeeValue: existingFlag.primaryEmployeeValue,
          organisationOverrides,
        },
      },
    });

    setNewOrgOverrideOpen(false);
    toast({
      kind: "success",
      title: `${flagName} org override created`,
      autoHide: true,
    });
  };

  const handleDeleteOrgOverride = async ({
    orgId,
    flagName,
  }: {
    orgId: string;
    flagName: string;
  }) => {
    const existingFlag = featureFlags.find((ff) => ff.name === flagName);
    if (!existingFlag) {
      throw new Error(`couldnt find flag ${flagName}`);
    }

    const organisationOverrides = (existingFlag.orgOverrides ?? [])
      .filter((o) => o.organisation?.id !== orgId)
      .map((o) => ({
        organisationId: o.organisation?.id ?? "",
        value: o.value,
      }));

    await updateFeatureFlag({
      variables: {
        input: {
          flagName,
          defaultValue: existingFlag.defaultValue,
          primaryEmployeeValue: existingFlag.primaryEmployeeValue,
          organisationOverrides,
        },
      },
    });

    setDeleteOrgOverrideOpen("");

    toast({
      kind: "success",
      title: `${flagName} org override deleted`,
      autoHide: true,
    });
  };

  const handleToggleOrgOverride = async ({
    orgId,
    flagName,
    newValue,
  }: {
    orgId: string;
    flagName: string;
    newValue: boolean;
  }) => {
    const existingFlag = featureFlags.find((ff) => ff.name === flagName);
    if (!existingFlag) {
      throw new Error(`couldnt find flag ${flagName}`);
    }

    const organisationOverrides = (existingFlag.orgOverrides ?? []).map(
      (o) => ({
        organisationId: o.organisation?.id ?? "",
        value: o.organisation?.id === orgId ? newValue : o.value,
      }),
    );

    await updateFeatureFlag({
      variables: {
        input: {
          flagName,
          defaultValue: existingFlag.defaultValue,
          primaryEmployeeValue: existingFlag.primaryEmployeeValue,
          organisationOverrides,
        },
      },
    });

    setDeleteOrgOverrideOpen("");

    toast({
      kind: "success",
      title: `${flagName} org override updated`,
      autoHide: true,
    });
  };

  const handleCreateFeatureFlag = async (name: string) => {
    if (name !== toSnakeCase(name)) {
      toast({
        kind: "error",
        title: "Flag name must be snake_case",
        autoHide: true,
      });
      return;
    }
    if (!name.match(/^[a-z0-9_]+$/)) {
      toast({
        kind: "error",
        title: "Flag name must be [a-z0-9_]",
        autoHide: true,
      });
      return;
    }

    await createFeatureFlag({
      variables: { input: { flagName: name } },
    });

    setNewFlagOpen(false);
    toast({ kind: "success", title: `${name} created`, autoHide: true });
  };

  const handleDeleteFeatureFlag = async (name: string) => {
    await deleteFeatureFlag({
      variables: { input: { flagName: name } },
    });

    setDeleteFlagOpen("");
    toast({ kind: "success", title: `${name} deleted`, autoHide: true });
  };

  const handleToggleFlag = async ({
    flagName,
    newValue,
  }: {
    flagName: string;
    newValue: boolean;
  }) => {
    const existingFlag = featureFlags.find((ff) => ff.name === flagName);
    if (!existingFlag) {
      throw new Error(`could not find flag: ${flagName}`);
    }

    await updateFeatureFlag({
      variables: {
        input: {
          flagName,
          defaultValue: newValue,
          primaryEmployeeValue: existingFlag.primaryEmployeeValue,
        },
      },
    });

    toast({
      kind: "success",
      title: `${flagName} default value updated`,
      autoHide: true,
    });
  };

  const handleToggleFlagPrimaryEmployeeOverride = async ({
    flagName,
    newValue,
  }: {
    flagName: string;
    newValue: boolean;
  }) => {
    const existingFlag = featureFlags.find((ff) => ff.name === flagName);
    if (!existingFlag) {
      return;
    }

    await updateFeatureFlag({
      variables: {
        input: {
          flagName,
          defaultValue: existingFlag.defaultValue,
          primaryEmployeeValue: newValue,
        },
      },
    });

    toast({
      kind: "success",
      title: `${flagName} primary employee value updated`,
      autoHide: true,
    });
  };

  return (
    <AdminPageContainer testId="admin-feature-flags">
      <AdminPageHeader
        title="Feature Flags"
        subtitle="Feature flag creation and management"
        buttons={
          <Popover
            open={newFlagOpen}
            onOpenChange={(nextOpen) => {
              setNewFlagOpen(nextOpen);
            }}
          >
            <PopoverTrigger>
              <Button>New feature flag</Button>
            </PopoverTrigger>
            <PopoverContent>
              <div className="mb-2">
                <Input
                  placeholder="feature_flag_name"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      void handleCreateFeatureFlag(e.currentTarget.value);
                    }
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>
        }
      />
      <Accordion type="single" collapsible>
        {featureFlags.map((ff) => (
          <AccordionItem key={ff.id} value={ff.id}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex w-full flex-row items-center justify-between p-2 font-title">
                <div className="flex flex-row items-center gap-4">
                  <div
                    className={cn(
                      ["size-5 rounded-full border-[1px] border-solid"],
                      ...getDotColorFromFlag(ff),
                    )}
                  ></div>
                  <div className="flex flex-row items-center gap-2 font-bold font-mono">
                    {ff.name}{" "}
                    <div
                      className="text-secondary"
                      onClick={(e) => {
                        navigator.clipboard.writeText(ff.name);
                        e.stopPropagation();
                        toast({
                          kind: "info",
                          autoHide: true,
                          title: "Copied to clipboard.",
                        });
                      }}
                    >
                      <PiCopy />
                    </div>
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2 text-secondary text-sm tabular-nums">
                  <div>Overrides </div>
                  <div className="flex flex-row items-center gap-2">
                    <PiUser />
                    {ff.userOverrides?.length ?? 0}
                  </div>
                  <div>·</div>
                  <div className="flex flex-row items-center gap-2">
                    <PiBuildingOffice />
                    {ff.orgOverrides?.length ?? 0}
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-2">
              <div className="mb-2 flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Txt variant="field-label">Base value</Txt>
                  <Switch
                    checked={ff.defaultValue}
                    onCheckedChange={(newValue) =>
                      handleToggleFlag({ flagName: ff.name, newValue })
                    }
                  />
                </div>
                <div className="flex flex-row items-center gap-2">
                  <div className="text-end text-secondary">Updated at</div>
                  <div>{formatLocalDateTime(ff.updatedAt)}</div>
                  <Popover
                    open={deleteFlagOpen == ff.id}
                    onOpenChange={(nextOpen) => {
                      if (!nextOpen) {
                        setDeleteFlagOpen("");
                      } else {
                        setDeleteFlagOpen(ff.id);
                      }
                    }}
                  >
                    <PopoverTrigger>
                      <RiSettings3Line />
                    </PopoverTrigger>
                    <PopoverContent>
                      <div className="flex w-full items-center justify-center">
                        <Button
                          className="w-full font-bold"
                          variant="destructive"
                          onClick={() => handleDeleteFeatureFlag(ff.name)}
                        >
                          Delete
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="flex flex-row items-stretch gap-4 p-2">
                <div className="flex-1">
                  <Card className="h-full">
                    <CardContent className="p-4">
                      <div className="pb-2">
                        <Txt variant="section-title">
                          Organisation Overrides
                        </Txt>
                      </div>

                      <div className="mb-4 flex flex-row items-center justify-between">
                        Primary Employees
                        <div className="flex flex-row items-center gap-4">
                          <Switch
                            checked={ff.primaryEmployeeValue}
                            onCheckedChange={(newValue) =>
                              handleToggleFlagPrimaryEmployeeOverride({
                                flagName: ff.name,
                                newValue,
                              })
                            }
                          />
                          <PiTrash className="text-tertiary text-xl" />
                        </div>
                      </div>

                      {ff.orgOverrides && ff.orgOverrides.length > 0 && (
                        <div>
                          {ff.orgOverrides.map((o) => (
                            <div
                              key={o.id}
                              className="mb-4 flex flex-row items-center justify-between"
                            >
                              {o.organisation?.name ?? "MISSING ORG"}
                              <div className="flex flex-row items-center gap-4">
                                <Switch
                                  checked={o.value}
                                  onCheckedChange={(newValue) =>
                                    handleToggleOrgOverride({
                                      flagName: ff.name,
                                      newValue,
                                      orgId: o.organisation?.id ?? "",
                                    })
                                  }
                                />
                                <Popover
                                  open={
                                    deleteOrgOverrideOpen === o.organisation?.id
                                  }
                                  onOpenChange={(nextOpen) => {
                                    if (!nextOpen) {
                                      setDeleteOrgOverrideOpen("");
                                    } else {
                                      setDeleteOrgOverrideOpen(
                                        o.organisation?.id ?? "",
                                      );
                                    }
                                  }}
                                >
                                  <PopoverTrigger>
                                    <PiTrash className="text-danger text-xl" />
                                  </PopoverTrigger>
                                  <PopoverContent>
                                    <Button
                                      className="w-full font-bold"
                                      variant="destructive"
                                      onClick={() =>
                                        handleDeleteOrgOverride({
                                          flagName: ff.name,
                                          orgId: o.organisation?.id ?? "",
                                        })
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex w-full flex-row justify-end">
                        <Popover
                          open={newOrgOverrideOpen}
                          onOpenChange={(nextOpen) => {
                            setNewOrgOverrideOpen(nextOpen);
                          }}
                        >
                          <PopoverTrigger>
                            <div className="flex size-5 items-center justify-center rounded-full bg-info font-bold text-white">
                              <PiPlus />
                            </div>
                          </PopoverTrigger>
                          <PopoverContent>
                            <Command
                              filter={(value, search) => {
                                let score = 0;
                                for (const w of search.split(" ")) {
                                  if (
                                    value
                                      .toLowerCase()
                                      .includes(w.toLowerCase())
                                  ) {
                                    score++;
                                  }
                                }
                                return score;
                              }}
                            >
                              <CommandInput placeholder="New org override..." />
                              <CommandList>
                                <CommandEmpty>
                                  No matching orgs found
                                </CommandEmpty>
                                <CommandGroup>
                                  {organisations
                                    .filter(
                                      (o) =>
                                        !(ff.orgOverrides ?? []).find(
                                          (oo) => oo.organisation?.id === o.id,
                                        ),
                                    )
                                    .map((o) => (
                                      <CommandItem
                                        key={o.id}
                                        value={`${o.id} ${o.name}`}
                                        onSelect={() => {
                                          void handleCreateOrgOverride({
                                            orgId: o.id,
                                            flagName: ff.name,
                                          });
                                        }}
                                      >
                                        {o.name}
                                      </CommandItem>
                                    ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="flex-1">
                  <Card className="h-full">
                    <CardContent className="p-4">
                      <div className="pb-2">
                        <Txt variant="section-title">User Overrides</Txt>
                      </div>
                      <div>🚧 Coming soon 🚧</div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </AdminPageContainer>
  );
}

function getDotColorFromFlag(flag: FeatureFlagConfigDetailsFragment) {
  if (flag.defaultValue) {
    return ["border-success bg-success"];
  }

  if (
    flag.primaryEmployeeValue ||
    flag.orgOverrides?.some((o) => o.value) ||
    flag.userOverrides?.some((o) => o.value)
  ) {
    return ["border-warning bg-warning"];
  }

  return ["border bg-neutral"];
}
